import { useMember } from "api/members";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { LinkItem } from "LinkItem";
import { useEffect, useState } from "react";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REFERRED_MEMBERS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions, Order } from "types/listing";
import { ReferredMember } from "types/members";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

function createItems(items: ReferredMember[]): Row[] {
  return items.map((item) => ({
    id: item.referralId,
    cells: [
      {
        content: (
          <LinkItem to={links.singleMemberGeneralInfo(item.referrerId)}>
            {item.referrerName}
          </LinkItem>
        ),
        property: "referrerName",
        dataHjSuppress: true,
      },
      {
        content: item.referrerToken,
        property: "referrerToken",
        dataHjSuppress: true,
      },
      {
        content: (
          <LinkItem to={links.singleMemberGeneralInfo(item.refereeId)}>
            {item.refereeName}
          </LinkItem>
        ),
        property: "refereeName",
        dataHjSuppress: true,
      },
      { content: OLDate(item.createdAt), property: "createdAt" },
    ],
  }));
}
export function ReferredMembersList() {
  const { message } = useMessage();
  const tables = useAppSelector(selectTables);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[REFERRED_MEMBERS_LIST_ID]?.filters || [],
  );
  const { useReferredMembers } = useMember();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const { listing, updateListing } = useListing(REFERRED_MEMBERS_LIST_ID);
  const appDispatch = useAppDispatch();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();

  const { data: referredMembers, error } = useReferredMembers({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      REFERRED_MEMBERS_LIST_ID,
    );

    setFilters(filters);
  };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "referrerName" }),
      property: "referrerName",
      isSortable: true,
    },
    {
      headerText: message({ id: "referrerToken" }),
      property: "referrerToken",
      isSortable: true,
    },
    {
      headerText: message({ id: "refereeName" }),
      property: "refereeName",
      isSortable: true,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      isSortable: true,
    },
  ]);
  const items = referredMembers?.items
    ? createItems(referredMembers.items)
    : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && referredMembers) {
      setPersistentFilters(filters, REFERRED_MEMBERS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, referredMembers, error]);

  return (
    <>
      <TopHeader title={message({ id: "referredMembers" })} />
      <Subheader>
        <FiltersV2
          data-test-id="referred-members"
          donutLabel={message({ id: "members" })}
          setFilters={setSelectedFilters}
          total={referredMembers?.total}
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "refereeName",
              label: message({ id: "refereeName" }),
              type: "text",
            },
            {
              query: "referrerName",
              label: message({ id: "referrerName" }),
              type: "text",
            },
            {
              query: "referrerToken",
              label: message({ id: "referrerToken" }),
              type: "text",
            },
          ]}
        />
        <DynamicSearch
          property="refereeName"
          properties={[
            {
              id: "refereeName",
              label: "refereeName",
            },
            { id: "referrerName", label: "referrerName" },
            { id: "referrerToken", label: "referrerToken" },
          ]}
          setDynamicSearch={setDynamicSearch}
          onSearch={() =>
            appDispatch(clearTablePage({ tableName: REFERRED_MEMBERS_LIST_ID }))
          }
        />
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={REFERRED_MEMBERS_LIST_ID}
        data-test-id="referred-members"
        columns={referredMembers && headItems}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        onSort={(prop) =>
          updateListing({
            payload: {
              ...prop,
              onSortCustomAction: (
                order: Order,
                property: string,
                rowsPerPage: number,
                page: number,
              ) => {
                setPersistentListingSorting(
                  order,
                  property,
                  rowsPerPage,
                  page,
                  REFERRED_MEMBERS_LIST_ID,
                );
              },
            },
            type: ListingActions.SET_SORT,
          })
        }
        rows={referredMembers && items}
        pagination={{
          count: calculateCount(
            referredMembers?.total.filtered,
            referredMembers?.total.all,
          ),
          page: listing.page,
          onChangePage: (page) => {
            updateListing({
              payload: page,
              type: ListingActions.CHANGE_PAGE,
            });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            });
          },
          rowsPerPage: listing.rowsPerPage,
        }}
        clearFilters={() => setFilters([])}
      />
    </>
  );
}
